import React, { useEffect, useMemo } from "react";
import {
  Button,
  Drawer,
  Flex,
  Image,
  Input,
  LegacyForm,
  Tooltip,
  useMessagedAsync,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { Close, Instagram, Telegram, Twitter } from "@payping/icons";
import { DashboardRoutes } from "@/router";
import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import { Link, useNavigate } from "react-router-dom";
import type { UpdateSocialInfoRequestModel } from "@payping/api";
import { getUserSocials, updateSocials } from "@payping/redux/actions";
import { URLS } from "@payping/utilities-shared/consts";

const MobileProfileSocial: React.FC = () => {
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const socials = useAppSelector((state) => state.user.socials);
  const navigate = useNavigate();
  const [form] = LegacyForm.useForm<UpdateSocialInfoRequestModel>();

  const { loading: loadingGet } = useMessagedAsync(
    async () => {
      await dispatch(getUserSocials());
    },
    [dispatch],
    { useProgress: true },
  );

  const [{ loading: loadingEdit }, handleEdit] = useMessagedAsyncFn(
    async (values: UpdateSocialInfoRequestModel) => {
      await dispatch(updateSocials(values));
      navigate(DashboardRoutes.profile.root.url);
    },
    [dispatch, navigate],
    undefined,
    { useProgress: true },
  );

  const loading = useMemo(() => loadingGet || loadingEdit, [loadingGet, loadingEdit]);

  useEffect(() => {
    form.setFieldsValue({
      instagram: socials?.instagram,
      telegram: socials?.telegram,
      twitter: socials?.twitter,
    });
  }, [form, socials]);

  return (
    <Drawer
      title={t("profile.main.social")}
      open
      size={"100%"}
      placement={"bottom"}
      closeIcon={false}
      extra={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            navigate(DashboardRoutes.profile.root.url);
          }}
        />
      }
      footer={
        <Flex gap={"small"} className="tw:flex tw:justify-end">
          <Link to={DashboardRoutes.profile.root.url}>
            <Button variant={"outlined"} color="primary">
              {t("return")}
            </Button>
          </Link>
          <Button type={"primary"} loading={loading} onClick={() => form.submit()} htmlType={"submit"}>
            {t("save")}
          </Button>
        </Flex>
      }
    >
      <LegacyForm<UpdateSocialInfoRequestModel> onFinish={handleEdit} form={form} layout="horizontal">
        <LegacyForm.Item className="tw:flex tw:justify-center">
          <Image preview={false} src={`${URLS.STORAGE_URL}/img/settings/settings-social-bg.png`} />
        </LegacyForm.Item>
        <LegacyForm.Item
          label={
            <Tooltip title={"Twitter"}>
              <Twitter width={"20px"} />
            </Tooltip>
          }
          name={"twitter"}
        >
          <Input disabled={loading} style={{ direction: "ltr" }} placeholder={"twitter"} />
        </LegacyForm.Item>
        <LegacyForm.Item
          label={
            <Tooltip title={"Telegram"}>
              <Telegram width={"20px"} />
            </Tooltip>
          }
          name={"telegram"}
        >
          <Input disabled={loading} style={{ direction: "ltr" }} placeholder={"telegram ID"} />
        </LegacyForm.Item>
        <LegacyForm.Item
          label={
            <Tooltip title={"Instagram"}>
              <Instagram width={"20px"} />
            </Tooltip>
          }
          name={"instagram"}
        >
          <Input disabled={loading} style={{ direction: "ltr" }} placeholder={"instagram"} />
        </LegacyForm.Item>
      </LegacyForm>
    </Drawer>
  );
};

export default MobileProfileSocial;
